import { Coercer, Environment, IEnvironmentConfig } from '@app/src/environment';
import { firebase } from '@app/src/firebase';

import { RosterstreamEnabledConfig } from '../services/auth/sso/sso.utils';

const getString = (key: RemoteConfigKey): string => firebase.remoteConfig().getString(key);
const getNumber = (key: RemoteConfigKey): number => firebase.remoteConfig().getNumber(key);
const getBoolean = (key: RemoteConfigKey): boolean => firebase.remoteConfig().getBoolean(key);
const getJson = <T>(key: RemoteConfigKey): T => {
  const value = JSON.parse(getString(key));
  return value;
};

export type RemoteConfig = Pick<
  IEnvironmentConfig,
  | 'mgsrvGatewayHost'
  | 'environment'
  | 'websiteAnalyticsId'
  | 'version'
  | 'joinCodeCollection'
  | 'assetUrlPrefix'
  | 'cleverEnableLogin'
  | 'minimumVersion'
  | 'idBucket'
  | 'suggestionUrl'
  | 'notificationsReminderDuration'
  | 'moduleUpgradeHubspotUrl'
  | 'kioskEnabled'
  | 'kioskLogoutRequiresPin'
  | 'countryAppUrls'
  | 'REMOTE_DEBUG'
  | 'studentScheduleEnabled'
  | 'iosAppStoreUrl'
  | 'androidAppStoreUrl'
  | 'bellScheduleEnabled'
  | 'rosterStreamSSO'
  | 'rosterStreamSSOEnabled'
  | 'classLinkSSOEnabled'
  | 'disableBranchTracking'
>;

type CoercedRemoteConfig = Coercer<RemoteConfig>;

/**
 * We need to coerce the values coming from remote config to the correct type.
 */
export const REMOTE_CONFIG_COERCED_MAPPER: CoercedRemoteConfig = {
  mgsrvGatewayHost: () => getString('mgsrvGatewayHost'),
  environment: () => getString('environment') as Environment,
  websiteAnalyticsId: () => getString('websiteAnalyticsId'),
  countryAppUrls: () => getJson('countryAppUrls'),
  version: () => getString('version'),
  joinCodeCollection: () => getString('joinCodeCollection'),
  assetUrlPrefix: () => getString('assetUrlPrefix'),
  cleverEnableLogin: () => getBoolean('cleverEnableLogin'),
  minimumVersion: () => getString('minimumVersion'),
  idBucket: () => getString('idBucket'),
  suggestionUrl: () => getString('suggestionUrl'),
  notificationsReminderDuration: () => getNumber('notificationsReminderDuration'),
  moduleUpgradeHubspotUrl: () => getString('moduleUpgradeHubspotUrl'),
  kioskEnabled: () => getBoolean('kioskEnabled'),
  kioskLogoutRequiresPin: () => getBoolean('kioskLogoutRequiresPin'),
  REMOTE_DEBUG: () => getString('REMOTE_DEBUG'),
  studentScheduleEnabled: () => getBoolean('studentScheduleEnabled'),
  iosAppStoreUrl: () => getString('iosAppStoreUrl'),
  androidAppStoreUrl: () => getString('androidAppStoreUrl'),
  bellScheduleEnabled: () => getBoolean('bellScheduleEnabled'),
  rosterStreamSSO: () => getJson('rosterStreamSSO'),
  rosterStreamSSOEnabled: () => getString('rosterStreamSSOEnabled') as RosterstreamEnabledConfig,
  classLinkSSOEnabled: () => getBoolean('classLinkSSOEnabled'),
  disableBranchTracking: () => getBoolean('disableBranchTracking'),
};

type RemoteConfigKey = keyof RemoteConfig;

export function IEnvironmentToRemoteConfig(config: IEnvironmentConfig): RemoteConfig & {
  // any json fields need to be stringified to be set as default config in remote config
  countryAppUrls: string;
} & { [key: string]: any } {
  const countryConfig = config.countryAppUrls;
  const countryAppUrls = JSON.stringify(countryConfig);
  const rosterStreamSSO = JSON.stringify(config.rosterStreamSSO);
  const newConfig = Object.assign({}, config, { countryAppUrls, rosterStreamSSO });
  delete newConfig.firebaseConfig;
  return newConfig;
}
